/*
  The V3 buy-box engine. ONE tested source, no imports. The inline copy is
  generated by shopify/scripts/build_inline_engine.cjs.

  It reads `data-*` off the server-rendered element and does nothing else. It
  never fetches a config document, never joins a registry, never computes money,
  and never reads the cart. Every axis a page can differ on is a `data-*` value
  written by `compile_buy_box.py` (spec section 2). If a page needs a branch in
  here, the SCHEMA is incomplete -- extend the schema, never this file.

  Shopify's own endpoints are NOT a page-variation axis, so the cart add paths,
  the checkout path and the route property key are CONSTANTS below rather than
  keys a surface has to supply (wave E, review §4).

  The required root keys are validated once, in `boot`. If any is missing the
  server-rendered box is left inert and the engine stops (N-9). That is the whole
  pre-mount gate: the CTA ships `disabled`, carries no form, and only this file
  can ever enable it.

  Invariants this file is responsible for: N-1 free-shipping subscription line,
  N-2 exactly one gift line, N-4 the cart is wiped at the tap, N-5 one AddToCart
  pixel per tap, N-6 One Time is one plain line, N-8 the route marker on every
  routed line, N-9 pre-mount tap does nothing, N-10 the adds are sequenced after
  that wipe inside one mutex entry, N-11 a failed write re-enables and explains,
  N-12 a sold-out card disables both CTAs.
*/
(function (globalScope) {
  "use strict";

  var REQUIRED = [
    // Wave F: the notice strings left this list with the DOM they used to
    // build. `buybox_surface.py` renders the notice itself now (R-G7), so the
    // compiler prints the English instead of passing it, and `surface_errors`
    // still refuses a surface that declares no notice at all -- correctness in
    // the compiler, not defence in the runtime (spec cause F-2).
    "routeMarker", "ctaLabel", "ctaSeparator",
    // N-1 is a HARD RULE and cannot rest on the absence of an attribute: the
    // surface declares its line set positively, and a declared line whose
    // identity is missing is a BOOT failure, never a quietly dropped line.
    "lines"
  ];
  var LINES = ["customer", "gift", "companion", "shipping"];
  var ROUTE_INTENT_ATTRIBUTE = "_cw_route_intent";
  var PURCHASE_SURFACE_ATTRIBUTE = "_cw_purchase_surface";
  // The cart-drawer countdown experiment's ORDER-grain arm carrier. This box
  // checks out directly, so a shopper who buys here never opens the drawer and
  // never used to receive a receipt: receipt coverage fell from about 92% to
  // 81% over 2026-09-11..09-19 for exactly this reason (countdown readout
  // 2026-09-20, defect 2). The receipt rides the box's OWN add request, because
  // the redirect that follows would outrun a separate cart-attribute write.
  var EXPERIMENT_RECEIPT_ATTRIBUTE = "cw_exp_receipt";
  var MARKER = /^bb-v[0-9]{1,3}-c[0-9]{1,4}$/;
  var NUMERIC = /^[1-9][0-9]{0,18}$/;
  // F-3. The second target the theme's own sticky bar watches (`theme.js:6042`).
  var FOOTER = ".shopify-section--footer";
  // Shopify's endpoints, pinned. A surface cannot move them and the compiler
  // cannot mistype them.
  var CART_ADD_PATH = "/cart/add.js";
  var CART_ADD_FORM_PATH = "/cart/add";
  var CART_CLEAR_PATH = "/cart/clear.js";
  var CHECKOUT_PATH = "/checkout";
  var ROUTE_PROPERTY_KEY = "_cw_route";
  // Attribution push A (plan 2026-09-28 §7). The page + mechanism stamp, built
  // by `window.__cwCtx` (assets/cw-ctx.js, loaded just before this file). Neither
  // Function reads it (S-7, CI-guarded), so unlike `_cw_route` it may ride a
  // One Time line without turning it into the gift. Evidence, never money.
  var CONTEXT_PROPERTY_KEY = "_cw_ctx";
  // The theme's queue is a plain promise chain (`theme.js:1525`): a mutation that
  // never settles wedges every other cart writer on the page for good. The
  // engine's write sequence holds one slot, so the SLOT -- never the write -- is
  // bounded. The engine's own chain keeps running after the bound expires and
  // still reports its own outcome; only the QUEUE is released, and the `busy`
  // lock still refuses a second tap. Releasing the slot early does let a foreign
  // write interleave, which is the deliberate trade: a wedged cart is worse.
  var MUTEX_HOLD_MS = 15000;
  // R1b (docs/research/boops-bb01-r-marker-4794-2026-09-29.md §7.1 item 2,
  // rulings R1-2 and R1-3). A failed write is retried automatically, inside the
  // SAME mutex entry and under the same `busy` lock, so the CTA stays busy: at
  // most three attempts in total, 250 ms before the second and 500 ms before
  // the third. Every attempt opens with `/cart/clear.js`, so an attempt never
  // builds on what the one before it left behind.
  var WRITE_ATTEMPTS = 3;
  var RETRY_BACKOFF_MS = [250, 500];
  // R1a (§7.1 item 1). The one beacon rail that reaches BigQuery:
  // `assets/observability.js` turns this document event into a
  // `cart_add_error` row. The two phases are the buy box's own, so its rows
  // stay apart from the product form's. R1d (BB-01 plan rev 3, ruling R1-5)
  // carries the write record too: which write failed last, its reason, the
  // attempt count and the write durations -- a FIXED set of scalar fields, so
  // the row stays small (`writeRecordDetail`). The full per-write list rides
  // only the `buybox:add` / `buybox:fail` events.
  var CART_ADD_ERROR_EVENT = "cw:cart_add_error";
  var PHASE_FAILED = "bb_failed";
  var PHASE_RECOVERED = "bb_retry_recovered";

  function numeric(value) {
    var text = String(value == null ? "" : value).trim();
    return NUMERIC.test(text) ? Number(text) : null;
  }

  function cartRoot(win) {
    var routes = win.Shopify && win.Shopify.routes;
    return (routes && routes.root) || "/";
  }

  function url(win, path) {
    return cartRoot(win) + String(path).replace(/^\//, "");
  }

  // A form POST to `/cart/add` is manually un-redirected, so Chromium hands
  // back an opaque response with status 0. Status alone cannot judge it. Same
  // acceptance test as `free-shipping.js` and V2; a rejected fetch is `null`.
  function formAccepted(response) {
    if (!response) return false;
    return response.type === "opaqueredirect" ||
      (response.status >= 200 && response.status < 400);
  }

  // `attributes` is optional. Only the R1b single-write retry sends them: it
  // is the only form write that can be the tap's first successful add.
  function formBody(items, attributes) {
    var fields = [];
    items.forEach(function (item, index) {
      fields.push(["items[" + index + "][id]", String(item.id)]);
      fields.push(["items[" + index + "][quantity]", String(item.quantity)]);
      if (item.selling_plan) {
        fields.push(["items[" + index + "][selling_plan]", String(item.selling_plan)]);
      }
      Object.keys(item.properties || {}).forEach(function (key) {
        fields.push([
          "items[" + index + "][properties][" + key + "]",
          String(item.properties[key])
        ]);
      });
    });
    Object.keys(attributes || {}).forEach(function (key) {
      var value = attributes[key];
      fields.push(["attributes[" + key + "]", value == null ? "" : String(value)]);
    });
    fields.push(["return_to", "/cart.js"]);
    return fields.map(function (pair) {
      return encodeURIComponent(pair[0]) + "=" + encodeURIComponent(pair[1]);
    }).join("&");
  }

  function createBuybox(env) {
    var win = env.window;
    var doc = env.document || win.document;
    var state = {
      root: null,
      config: null,
      cards: [],
      selected: null,
      cta: null,
      oneTimeButton: null,
      sticky: null,
      observer: null
    };

    // A box that is re-rendered is a NEW element, so a fresh engine boots
    // against the replacement. The one thing that must outlive the element lives on the
    // window: `busy`, because two live CTAs would be two paid lines.
    function shared() {
      if (!win.__cwBuybox) win.__cwBuybox = { busy: false };
      return win.__cwBuybox;
    }

    // The two diagnostic events: `add` and `fail`. `select` carries trusted
    // card changes to purchase-page-analytics.liquid. Other `buybox:*`
    // events were deleted in wave E for having no listener; `add` is kept for
    // the LP telemetry contract (plan 2026-09-21 §3 W3, §1.3). R1a
    // (docs/research/boops-bb01-r-marker-4794-2026-09-29.md §7.1 item 1)
    // restored `fail`, and both now carry the tap record (`tapRecord`). No
    // listener reads them yet; the BigQuery rail is `cw:cart_add_error`.
    // `add` no longer feeds `checkout_entered`: R-13 (2026-09-23,
    // docs/plans/boops-buybox-v3-simplification-plan-2026-09-22.md §0.1) made
    // `snippets/cart-drawer-analytics.liquid` the single owner of that event
    // and retired the `assets/buybox-telemetry.js` listener that used to
    // rebuild it on top of `add` (it duplicated the event with the wrong,
    // pre-V3 surface — Status L-2). `assets/buybox-telemetry.js` keeps only
    // `resumeAcquisition()` on load, unrelated to this event.
    function emit(name, detail) {
      if (!state.root || typeof win.CustomEvent !== "function") return;
      state.root.dispatchEvent(new win.CustomEvent("buybox:" + name, {
        bubbles: true, detail: detail || {}
      }));
    }

    // ---- boot -------------------------------------------------------------

    function readConfig(root) {
      var data = root.dataset;
      var config = {};
      var missing = false;
      REQUIRED.forEach(function (key) {
        var value = data[key];
        if (typeof value !== "string" || !value) { missing = true; return; }
        config[key] = value;
      });
      if (missing || !MARKER.test(config.routeMarker)) return null;
      config.lines = config.lines.split(",");
      if (config.lines[0] !== "customer" ||
          config.lines.some(function (line) { return LINES.indexOf(line) < 0; })) {
        return null;
      }
      if (new Set(config.lines).size !== config.lines.length ||
          (config.lines.indexOf("gift") >= 0 && config.lines.indexOf("companion") >= 0)) return null;
      config.companionVariantId = numeric(data.companionVariantId);
      config.companionPlanPairs = (data.companionPlanPairs || "").split(",");
      if (config.lines.indexOf("companion") >= 0 && config.companionPlanPairs.some(function (pair) {
        var ids = pair.split(":");
        return ids.length !== 2 || !numeric(ids[0]) || !numeric(ids[1]);
      })) return null;
      if (config.lines.indexOf("companion") >= 0 && !config.companionVariantId) return null;
      config.giftVariantId = numeric(data.giftVariantId);
      config.shipping = boxShipping(data);
      config.fsIdentityGlobal = data.fsIdentityGlobal || "";
      if (config.lines.indexOf("gift") >= 0 && !config.giftVariantId) return null;
      if (config.lines.indexOf("shipping") >= 0 &&
          (config.shipping === false || (!config.shipping && !config.fsIdentityGlobal))) {
        return null;
      }
      config.sticky = data.sticky === "1";
      config.oneTimeVariantId = numeric(data.oneTimeVariantId);
      // Telemetry only, and therefore OPTIONAL. A surface that loses this
      // attribute goes dark in GA4; it still sells. Nothing about a missing
      // measurement may fail boot.
      config.surfaceId = typeof data.surfaceId === "string" ? data.surfaceId : "";
      // Attribution only, and OPTIONAL for the same reason: `data-cw-surface`
      // (`bb.<surface_id>.main`) names this box's `_cw_ctx` surface. The helper
      // validates it at tap time; a missing or malformed one costs the stamp.
      config.cwSurface = typeof data.cwSurface === "string" ? data.cwSurface : "";
      return config;
    }

    // Wave 3 (plan 2026-09-22 §1.3). The free-shipping line's identity is a
    // fact of the BOX: `snippets/buybox-offer-<offer_id>.liquid` renders it as
    // four `data-fs-*` attributes, projected by `project_offer.py` from the same
    // registry (`shared/shared/system_benefits.yaml`) that
    // `free-shipping-line-config.liquid` mirrors into `window.__cwFreeShipping`.
    // So the tap no longer depends on that global join. The drawer and the PDP
    // interceptor still read the global; nothing here touches it (R-5).
    //
    // Three answers: `null` when the box declares none of the four (the live
    // factory markup rendered by `buybox_surface.py`, which keeps today's global
    // path until wave 4 switches those pages); the facts when all four are
    // present and well formed; `false` -- a BOOT failure (N-9) -- when the box
    // declares some of them, or declares them malformed. A half-declared line is
    // a generator bug, and N-1 never rests on guessing which half is right.
    function boxShipping(data) {
      var keys = ["fsVariantId", "fsPlanId", "fsProperty", "fsPropertyValue"];
      var declared = keys.filter(function (key) { return data[key] !== undefined; });
      if (!declared.length) return null;
      var facts = {
        variantId: numeric(data.fsVariantId),
        planId: numeric(data.fsPlanId),
        property: typeof data.fsProperty === "string" ? data.fsProperty.trim() : "",
        propertyValue: typeof data.fsPropertyValue === "string" ? data.fsPropertyValue.trim() : ""
      };
      if (declared.length !== keys.length || !facts.variantId || !facts.planId ||
          !facts.property || !facts.propertyValue) return false;
      return facts;
    }

    function readCard(button) {
      var data = button.dataset;
      return {
        element: button,
        id: data.cardId || "",
        variantId: numeric(data.variantId),
        sellingPlanId: numeric(data.sellingPlanId),
        companionPlanId: numeric(data.companionPlanId),
        companionQuantity: numeric(data.companionQuantity),
        quantity: numeric(data.quantity),
        price: data.price || "",
        strikePrice: data.strikePrice || "",
        companionDeclared: Object.prototype.hasOwnProperty.call(data, "companionPlanId") ||
          Object.prototype.hasOwnProperty.call(data, "companionQuantity"),
        available: data.available === "1"
      };
    }

    // N-9. Anything short of a complete, valid config leaves the inert
    // server-rendered box exactly as it is. No partial mount, no CTA, and no
    // diagnostic event: a boot failure is proved by the box staying inert.
    //
    // A boot failure never releases the lock either: a write may still be in
    // flight, and the next boot that DID succeed would enable a CTA beside it
    // and send a second customer line. A lock is released by whoever took it.
    function boot(root) {
      var config = readConfig(root);
      if (!config) return false;
      var buttons = Array.prototype.slice.call(
        root.querySelectorAll("[data-buybox-card]")
      );
      var cards = buttons.map(readCard).filter(function (card) {
        var paired = config.lines.indexOf("companion") >= 0 && config.companionPlanPairs.some(function (pair) {
          return pair.split(":")[0] === String(card.sellingPlanId);
        });
        return card.id && card.variantId && card.sellingPlanId && card.quantity &&
          (paired ? (card.companionPlanId && card.companionDeclared &&
            card.companionPlanId !== card.sellingPlanId && card.companionQuantity === 1 &&
            config.companionPlanPairs.indexOf(card.sellingPlanId + ":" + card.companionPlanId) >= 0 &&
            config.companionVariantId !== card.variantId) : !card.companionDeclared);
      });
      if (cards.length !== buttons.length || !cards.length) return false;
      teardown();
      state.root = root;
      state.config = config;
      state.cards = cards;
      state.cta = root.querySelector("[data-buybox-cta]");
      state.oneTimeButton = root.querySelector("[data-buybox-one-time]");
      state.sticky = root.querySelector("[data-buybox-sticky]");
      if (!state.cta) return false;
      cards.forEach(function (card) {
        card.element.disabled = false;
        card.element.addEventListener("click", function (event) { select(card.id, event.isTrusted); });
      });
      state.cta.addEventListener("click", tap);
      if (state.oneTimeButton) state.oneTimeButton.addEventListener("click", oneTime);
      var fieldset = root.querySelector("[data-buybox-cards]");
      if (fieldset) fieldset.removeAttribute("aria-busy");
      // FINDING A-4 (adversarial review 2026-09-18). The recalled card used to
      // come from ONE `sessionStorage` key shared by every surface, and both V3
      // pages ship identical card ids, so the 3-month page opened on whatever
      // the shopper had touched on the 1-month page. Recall is not an invariant
      // -- the surface declares its own default -- so it is DELETED rather than
      // keyed by surface (RS-3 / review §4). The opening card is exactly what
      // the server rendered as pressed: the surface's `default_card_id`.
      select((cards.filter(function (card) {
        return card.element.getAttribute("aria-pressed") === "true";
      })[0] || cards[0]).id);
      renderSticky();
      reportReady();
      return true;
    }

    // P1C (docs/plans/boops-buybox-page-load-audit-and-plan-2026-10-08.md §6 item
    // 11). The moment the CTA can be tapped, recorded ONCE per page load: time
    // since navigation start (`performance.now()` shares the navigation's time
    // origin), plus the selected card as the variant. Telemetry only: it reads
    // state, writes two window globals and calls the existing observability
    // bridge; it never touches the cart, the CTA, an event or the lock, and any
    // failure is swallowed. `window.__cwDw` is the inline dead-window probe
    // (snippets/buybox-deadwindow-probe.liquid), absent on every other page.
    // A sold-out opening card leaves the CTA disabled, so that is not "ready".
    function reportReady() {
      try {
        if (win.__cwBuyboxReadyMs != null || state.cta.disabled) return;
        if (!win.performance || typeof win.performance.now !== "function") return;
        var ms = Math.round(win.performance.now());
        win.__cwBuyboxReadyMs = ms;
        var probe = win.__cwDw;
        if (probe && typeof probe === "object") {
          probe.r = ms;
          probe.v = state.selected ? String(state.selected.id) : "";
        }
        if (typeof win.__cwObsvTiming === "function") win.__cwObsvTiming("buybox_ready_ms", ms);
      } catch (e) { /* telemetry must never block the buy box */ }
    }

    // D-1. The label and the compare-at strike V2's `updateCta` writes into the
    // theme button. Both prices are server strings; the engine never computes.
    function writeCta(button, card) {
      var label = doc.createElement("span");
      label.className = "premium-anchor-v2__cta-label";
      label.textContent = state.config.ctaLabel + state.config.ctaSeparator + card.price;
      var strike = doc.createElement("s");
      strike.className = "premium-anchor-v2__cta-strike";
      strike.textContent = card.strikePrice;
      button.replaceChildren(label, strike);
      button.setAttribute("aria-label", label.textContent + " " + strike.textContent);
    }

    // ---- select -----------------------------------------------------------

    // bb-v9 opens the selected card's benefits. Earlier routes retain their
    // server-rendered stack behavior (operator ruling 2026-10-23).
    function select(cardId, userChange) {
      var card = state.cards.filter(function (item) { return item.id === cardId; })[0];
      if (!card) return null;
      var changed = state.selected && state.selected.id !== card.id;
      state.selected = card;
      state.cards.forEach(function (other) {
        var chosen = other === card;
        other.element.setAttribute("aria-pressed", chosen ? "true" : "false");
        if (/^bb-v9-c[0-9]{1,4}$/.test(state.config.routeMarker)) {
          var stack = other.element.querySelector("[data-premium-anchor-v2-value-stack]");
          if (stack) stack.hidden = !chosen;
        }
      });
      writeCta(state.cta, card);
      availability();
      syncSticky();
      if (changed && userChange) emit("select", selectionContext(card));
      return card;
    }

    function selectionContext(card) {
      return {
        offer_id: state.config.routeMarker.split("-c")[0],
        card_id: card.id,
        // The rendered offers sell 30-day tubs (one or three per card).
        supply_days: card.quantity * 30,
        selling_plan_id: String(card.sellingPlanId),
        variant_id: String(card.variantId),
        quantity: card.quantity
      };
    }

    // N-12. Availability is a server-rendered fact per card. The engine only
    // mirrors it onto the two CTAs; it never asks the network whether a variant
    // is in stock. The CARDS follow the lock too, or the shopper could pick a
    // different plan while the write for the previous one was still in the air.
    function availability() {
      var sold = Boolean(state.selected) && !state.selected.available;
      state.cta.disabled = sold || busy();
      if (state.oneTimeButton) state.oneTimeButton.disabled = sold || busy();
      state.cards.forEach(function (card) { card.element.disabled = busy(); });
      return !sold;
    }

    // ---- flavour ----------------------------------------------------------

    // The flavour picker changes the product VARIANT, and every card on these
    // surfaces sells that one variant (`buybox_variant.id`). The box is kept
    // across the change (see `keepBox` in `mount`), so the engine re-points
    // the cards, the One Time line and availability at the variant the theme
    // announced. Plans, quantities and prices are per card and do not move: a
    // flavour change never touches `_cw_route`, the selling plan or the price.
    // A combination with no variant leaves nothing to buy, so the CTAs close.
    function setVariant(variant) {
      if (!state.root) return false;
      var id = variant ? numeric(variant.id) : null;
      var available = Boolean(id) && variant.available === true;
      state.cards.forEach(function (card) {
        if (id) {
          card.variantId = id;
          card.element.setAttribute("data-variant-id", String(id));
        }
        card.available = available;
        card.element.setAttribute("data-available", available ? "1" : "0");
      });
      if (id && state.config.oneTimeVariantId) {
        state.config.oneTimeVariantId = id;
        state.root.setAttribute("data-one-time-variant-id", String(id));
      }
      availability();
      syncSticky();
      return Boolean(id);
    }

    // ---- sticky -----------------------------------------------------------

    // N-9 again, for the second CTA. The bar and its button are server-rendered
    // (D-2), inert and `hidden`; boot is the only thing that enables them. The
    // engine builds no markup here -- it shows and enables what Liquid rendered.
    // The button carries no form of its own and delegates to the same `tap`, so
    // the sticky bar can never take a different path than the card CTA.
    function renderSticky() {
      if (!state.config.sticky || !state.sticky) return null;
      var clone = state.sticky.querySelector("[data-buybox-sticky-cta]");
      if (!clone) return null;
      clone.addEventListener("click", tap);
      state.sticky.hidden = false;
      syncSticky();
      watchSticky();
      return clone;
    }

    // D-2, corrected by F-3. The theme's rule is an AND of TWO observations
    // (`onFormVisibilityChange`, theme.js): the buy button has scrolled off the
    // TOP of the view AND the footer is not in view -- without the second half
    // the bar sits on top of the footer for the rest of the scroll. F-4: one
    // observer per box, the previous one disconnected first, exactly as V2's
    // element does in `disconnectedCallback`. No observer means no bar, never a
    // bar that will not leave. A page with no footer has nothing to suppress it.
    function teardown() {
      if (state.observer) state.observer.disconnect();
      state.observer = null;
      if (state.footerRetryListener) {
        doc.removeEventListener("readystatechange", state.footerRetryListener);
        state.footerRetryListener = null;
      }
      state.footerRetry = false;
      state.root = null;
    }

    function watchSticky() {
      if (state.observer) state.observer.disconnect();
      // Keep an already-visible bar until the new observer has geometry.
      var root = state.root;
      if (!win.IntersectionObserver) return;
      var footer = doc.querySelector(FOOTER);
      // The inline engine can boot before the footer is parsed. Retry once at
      // end of parse, before deferred scripts, and disconnect this observer.
      if (!footer && doc.readyState === "loading" && !state.footerRetry) {
        state.footerRetry = true;
        var parsed = function () {
          if (doc.readyState === "loading") return;
          doc.removeEventListener("readystatechange", parsed);
          state.footerRetryListener = null;
          if (state.root === root && doc.querySelector("[data-buybox]") === root) watchSticky();
        };
        state.footerRetryListener = parsed;
        doc.addEventListener("readystatechange", parsed);
      }
      var past = false;
      var clear = !footer;
      state.observer = new win.IntersectionObserver(function (entries) {
        if (state.root !== root) return;
        entries.forEach(function (entry) {
          if (entry.target === state.cta) {
            past = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
          } else {
            clear = !entry.isIntersecting;
          }
        });
        state.sticky.classList.toggle("is-visible", past && clear);
      });
      state.observer.observe(state.cta);
      if (footer) state.observer.observe(footer);
    }

    // R-G1. The bar's label is the theme bar's literal and never moves: V2's
    // sticky button is outside the form, so `updateCta` cannot reach it either.
    function syncSticky() {
      if (!state.sticky) return;
      var clone = state.sticky.querySelector("[data-buybox-sticky-cta]");
      if (clone) clone.disabled = state.cta.disabled;
    }

    // ---- writes -----------------------------------------------------------

    // Every other cart writer on this theme serialises through the theme's own
    // mutex (`theme.js:1568`). When the engine did not, the free-shipping
    // sweep's `/cart/change.js` and a clear ran at the same time, the clear
    // answered with an empty cart, the sweep's older snapshot put the lines
    // back, and the add MERGED into them -- two tubs and two gifts for one tap.
    //
    // The queue serialises the writers that JOIN it -- this engine's entries and
    // the theme's. It is not a lock on the cart, and the slot bound below only
    // stops one wedged mutation from freezing every other writer for good.
    function bounded(running) {
      if (typeof win.setTimeout !== "function") return running;
      return new Promise(function (resolve) {
        var settled = false;
        var timer;
        var settle = function () {
          if (!settled) {
            settled = true;
            if (typeof win.clearTimeout === "function") win.clearTimeout(timer);
            resolve();
          }
        };
        timer = win.setTimeout(settle, MUTEX_HOLD_MS);
        running.then(settle, settle);
      });
    }

    function themeSerialized(work) {
      var mutex = win.themeCartMutex;
      if (!mutex || typeof mutex.enqueue !== "function") return work();
      var running = null;
      try {
        var slot = mutex.enqueue(function () {
          running = Promise.resolve().then(work);
          return bounded(running);
        });
        // The caller awaits the REAL work, not the bounded slot, so a tap that
        // outlives its slot still reports its own outcome. A mutex that never ran
        // the entry is a broken mutex, and the tap runs unserialised rather than
        // resolving a purchase that never happened.
        return Promise.resolve(slot).then(function () {
          return running === null ? work() : running;
        });
      } catch (error) {
        return running === null ? work() : running;
      }
    }

    function serialized(work) {
      var writer = win.__cwCartAttributeWriter;
      if (!writer || typeof writer.runCartTransaction !== "function") {
        return themeSerialized(work);
      }
      var running = null;
      var expired = false;
      var slot;
      try {
        // Lock order: attribute queue, then theme mutex. The attribute writer
        // never takes the theme mutex. Hold the slot for the entire sequence.
        slot = writer.runCartTransaction(function () {
          // A timed-out wait must never turn into a second, late purchase.
          if (expired) return;
          running = Promise.resolve().then(function () { return themeSerialized(work); });
          return bounded(running);
        });
      } catch (error) {
        expired = true;
        return running === null ? themeSerialized(work) : running;
      }
      // Bound the WAIT as well as the held slot. A wedged attribute request
      // cannot freeze a tap; after the bound we accept possible interleaving,
      // exactly as for a wedged theme mutex. Await the real purchase outcome.
      return bounded(Promise.resolve(slot).then(function () {}, function () {})).then(function () {
        if (running !== null) return running;
        expired = true;
        return themeSerialized(work);
      });
    }

    // N-1, the pre-wave-3 path. A box with no `data-fs-*` facts (the three
    // live pages until wave 4) still takes the line identity from the carrier's
    // global and still requires it `armed`, exactly as before. Wave 5 deletes
    // this with the factory markup.
    function globalShipping() {
      var identity = win[state.config.fsIdentityGlobal];
      var variantId = identity && numeric(identity.variantId);
      var planId = identity && numeric(identity.planId);
      if (!identity || !identity.armed || !variantId || !planId ||
          typeof identity.property !== "string" || !identity.property ||
          typeof identity.propertyValue !== "string" || !identity.propertyValue) {
        return null;
      }
      return {
        variantId: variantId, planId: planId,
        property: identity.property, propertyValue: identity.propertyValue
      };
    }

    // The `_cw_ctx` value for a line of this box, or null. Measurement never
    // blocks a sale: no helper, a malformed surface or a throwing helper all
    // mean "no stamp", and the write goes ahead exactly as before push A.
    function contextStamp(mechanism, cardId) {
      try {
        var helper = win.__cwCtx;
        if (!helper || typeof helper.stamp !== "function") return null;
        var value = helper.stamp(state.config.cwSurface, mechanism, cardId);
        return typeof value === "string" && value ? value : null;
      } catch (error) { return null; }
    }

    // N-8: the route marker is on every routed line. N-2: exactly one gift line.
    // N-1: the free-shipping SUBSCRIPTION line item, whose identity comes from
    // the box's own `data-fs-*` facts (wave 3), or -- on markup that predates
    // them -- from the carrier's global (`globalShipping`).
    //
    // Push A: the customer, gift and free-shipping lines all carry ONE `_cw_ctx`
    // (`m=bb_subscribe`, `c=<card>`) beside `_cw_route`. The cart was cleared
    // at the tap (N-4), so no existing line can merge with them (S-5 is moot).
    function checkoutItems() {
      var config = state.config;
      var card = state.selected;
      var properties = {};
      properties[ROUTE_PROPERTY_KEY] = config.routeMarker;
      var stamp = contextStamp("bb_subscribe", card.id);
      if (stamp) properties[CONTEXT_PROPERTY_KEY] = stamp;
      var items = [{
        id: card.variantId,
        quantity: card.quantity,
        selling_plan: card.sellingPlanId,
        properties: Object.assign({}, properties)
      }];
      if (card.companionPlanId) {
        items.push({id: config.companionVariantId, quantity: card.companionQuantity,
          selling_plan: card.companionPlanId, properties: Object.assign({}, properties)});
      }
      if (config.lines.indexOf("gift") >= 0) {
        items.push({
          id: config.giftVariantId,
          quantity: 1,
          properties: Object.assign({}, properties)
        });
      }
      if (config.lines.indexOf("shipping") >= 0) {
        var identity = config.shipping || globalShipping();
        if (!identity) {
          // HARD RULE. A subscription order without the free-shipping line is
          // not a cheaper order, it is a WRONG one. The tap fails loudly instead
          // of quietly selling a plan the shopper was not shown.
          throw new Error("offer_shipping_not_armed");
        }
        var fsProperties = Object.assign({}, properties);
        fsProperties[identity.property] = identity.propertyValue;
        items.push({
          id: identity.variantId, quantity: 1, selling_plan: identity.planId,
          properties: fsProperties
        });
      }
      return items;
    }

    // assets/theme.js owns the assignment and exposes it on `window.__cwCartExp`.
    // theme.js is a head module and this file is a body `defer` script, so the
    // global is present by tap time. It is still read defensively: an absent
    // global costs a receipt, never a sale.
    function experimentReceipt() {
      try {
        var api = win.__cwCartExp;
        if (!api || typeof api.receiptJson !== "function") return "";
        var receipt = api.receiptJson();
        return typeof receipt === "string" ? receipt : "";
      } catch (error) { return ""; }
    }

    function withReceipt(attributes) {
      var receipt = experimentReceipt();
      if (receipt) attributes[EXPERIMENT_RECEIPT_ATTRIBUTE] = receipt;
      return attributes;
    }

    // Push A (audit A2 D-1, the one V3 hook still missing). The session writer
    // in snippets/marketing-click-ids.liquid batches its `cw_*` cart attributes
    // for 250 ms, and this tap redirects to checkout, so a tap inside that
    // window used to leave the session facts behind. Whatever the writer has
    // not flushed yet rides this add instead, as V2's box did. The box's own
    // keys are written after the merge, so they always win.
    function pendingSessionAttributes() {
      try {
        var writer = win.__cwCartAttributeWriter;
        if (!writer || typeof writer.purchaseAttributes !== "function") return {};
        var pending = writer.purchaseAttributes();
        return pending && typeof pending === "object" ? Object.assign({}, pending) : {};
      } catch (error) { return {}; }
    }

    function checkoutAttributes() {
      var attributes = pendingSessionAttributes();
      attributes[ROUTE_INTENT_ATTRIBUTE] = state.config.routeMarker;
      attributes[PURCHASE_SURFACE_ATTRIBUTE] = state.config.routeMarker + "|subscription";
      return withReceipt(attributes);
    }

    // N-6. One Time is an UNROUTED order. `/cart/clear.js` leaves attributes
    // alone, so the One Time add blanks both of them itself rather than leaving a
    // previous subscription route attached to a plain line.
    function oneTimeAttributes() {
      var attributes = pendingSessionAttributes();
      attributes[ROUTE_INTENT_ATTRIBUTE] = "";
      attributes[PURCHASE_SURFACE_ATTRIBUTE] = "";
      return withReceipt(attributes);
    }

    function jsonWrite(items, attributes) {
      attributes = withReaderSignature(attributes);
      return win.fetch(url(win, CART_ADD_PATH), {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({ items: items, attributes: attributes })
      }).catch(function () { return null; });
    }

    // N-4 / R-1. EVERY purchase entry opens with this: ONE `/cart/clear.js`
    // write, no read, and the answering body IS the resulting cart, so the clear
    // proves itself for free and the happy path still costs 0 readbacks. It sits
    // inside the tap's mutex entry, so this engine writes nothing else between
    // the clear and the adds. This is the whole of "the cart is wiped": the
    // separate ARRIVAL clear was deleted in wave E (RS-3 / A-7) because it cost
    // two GETs, committed in a DIFFERENT mutex entry from the writes it was
    // supposed to protect, and could only ever refuse a healthy tap.
    //
    // `/cart/clear.js` does not touch cart attributes, so the adds that follow
    // always carry the route attributes they want: the subscription route marker,
    // or -- on One Time -- both attributes as empty strings, so a stale
    // subscription route can never ride along on a One Time order.
    //
    // R1a: a body that is not JSON cannot prove the clear either, so it is
    // `cart_clear_incomplete` like any other body that is not an empty cart.
    function clearForPurchase() {
      return win.fetch(url(win, CART_CLEAR_PATH), {
        method: "POST", credentials: "same-origin",
        headers: { Accept: "application/json", "Content-Type": "application/json" }
      }).catch(function () { return null; }).then(function (response) {
        if (!response || !response.ok) throw writeError("cart_clear_failed", response);
        return Promise.resolve().then(function () { return response.json(); })
          .catch(function () { return null; })
          .then(function (after) {
            if (!after || !Array.isArray(after.items) || after.items.length) {
              throw writeError("cart_clear_incomplete", response);
            }
            return response.status;
          });
      });
    }

    function formWrite(items, attributes) {
      attributes = withReaderSignature(attributes);
      return win.fetch(url(win, CART_ADD_FORM_PATH), {
        method: "POST", credentials: "same-origin", redirect: "manual",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formBody(items, attributes)
      }).catch(function () { return null; });
    }

    // Refresh only this private cart key at EVERY actual add, after the clear
    // and on system-line/form retries too. Missing/failed/off/denied capture
    // must explicitly clear stale cart evidence; cart/clear retains attributes.
    function withReaderSignature(attributes) {
      var fresh = Object.assign({}, attributes);
      var value = "";
      try {
        var writer = win.__cwCartAttributeWriter;
        if (writer && typeof writer.readerSignatureAttribute === "function") {
          var sample = writer.readerSignatureAttribute();
          if (typeof sample === "string") value = sample;
        }
      } catch (error) { /* Measurement failure never retries commerce. */ }
      fresh.__cw_reader_sig = value;
      return fresh;
    }

    // R1a. A failed write carries the HTTP status that failed it: a number, or
    // null when no response came back at all (a network error).
    function writeError(reason, response) {
      var error = new Error(reason);
      error.status = response ? (Number(response.status) || 0) : null;
      return error;
    }

    // Write 1: the customer line alone, on the JSON route (the one AddToCart).
    function customerWrite(items, attributes) {
      return jsonWrite(items, attributes).then(function (response) {
        if (!response || !response.ok) throw writeError("cart_add_failed", response);
        return response.status;
      });
    }

    // Write 2, and the R1b single retry write: the form route, no pixel event.
    function formLinesWrite(items, attributes) {
      return formWrite(items, attributes).then(function (response) {
        if (!formAccepted(response)) throw writeError("cart_add_failed", response);
        return Number(response.status) || 0;
      });
    }

    // The lock lives on the WINDOW, not on this engine: a re-render that
    // replaces the box mid-purchase and a per-engine flag would leave the fresh
    // CTA enabled while the first write was still in flight.
    function setBusy(value) {
      shared().busy = Boolean(value);
      availability();
      syncSticky();
    }

    function busy() {
      return Boolean(shared().busy);
    }

    // A tap navigates away. Coming back restores this same JS context from the
    // back/forward cache with the lock still taken by a purchase that has
    // already left the page, so `pageshow` releases it. That is all this
    // handler does now: the arrival clear it used to re-run is gone.
    function onPageShow() {
      if (!state.root) return;
      setBusy(false);
    }

    // N-5. One shopper tap must report exactly ONE AddToCart. A single JSON add
    // carrying three lines emits one pixel event per LINE, which is the Meta
    // inflation WP-F measured. So the write is split by INTENT: the customer
    // line alone on the JSON route (one pixel), the system lines on the form
    // route (no pixel at all). The customer line goes first, so a half-written
    // offer never leaves a bare gift standing on its own.
    //
    // R-1. EVERY purchase is clear -> add -> add -> redirect, all inside ONE
    // mutex entry. A tap that reached the writes and then failed leaves the cart
    // in an UNKNOWN state -- write 1 can commit while write 2 does not -- and an
    // unconditional clear makes a retry just another tap: idempotent, no
    // ownership algebra, no dead end. The clear is one write whose body proves
    // it, so the path is 3 writes and 0 readbacks. R1b (`writeOffer`) retries
    // inside the same entry; its one flag decides only whether a retry may
    // send the JSON write again (N-5), never what the cart holds.
    //
    // N-6. One Time is the same path with a different plan: one plain line, no
    // gift, no free-shipping line, no route marker. The Functions ignore an
    // unmarked line, so Shopify charges the list price plus shipping -- which is
    // the whole promise of the mode.
    //
    // Push A: the line carries `_cw_ctx` (`m=bb_one_time`, no card) and still
    // no `_cw_route`. `_cw_route` on a one-time line is what marks the $0 gift
    // (line-item.liquid, buy-box-orphan-cleanup.liquid, both Functions);
    // `_cw_ctx` is read by none of them, so the line stays a paid list-price
    // line and One Time orders become attributable for the first time.
    function oneTimeItems() {
      var item = { id: state.config.oneTimeVariantId, quantity: 1 };
      var stamp = contextStamp("bb_one_time", null);
      if (stamp) {
        item.properties = {};
        item.properties[CONTEXT_PROPERTY_KEY] = stamp;
      }
      return [item];
    }

    // ---- R1a: measure one tap ---------------------------------------------

    function now() {
      var clock = win.performance;
      return clock && typeof clock.now === "function" ? clock.now() : Date.now();
    }

    // The tap record (§7.1 item 1). `writes` holds one entry per write, in
    // order: `{ attempt, write, ok, status, ms, reason }`. `write` is `clear`,
    // `write1` (JSON, customer line), `write2` (form, system lines) or
    // `write_all` (form, ALL lines -- the R1b retry after an ok write 1).
    // `status` is the HTTP status, or null for no response. `reason` is the
    // `fail()` reason of a failed write and "" for an ok one. `retryStopped`
    // is set only when a retry was skipped to stay inside the mutex slot.
    //
    // Built after the lock is taken, so it may never throw: a missing
    // selection costs the card id, never the lock.
    function tapRecord(oneTimeMode) {
      var card = oneTimeMode ? null : state.selected;
      var record = { cardId: card ? card.id : null, lines: 0 };
      if (oneTimeMode) record.oneTime = true;
      record.attempts = 0;
      record.writes = [];
      return record;
    }

    function timed(record, attempt, write, send) {
      var entry = { attempt: attempt, write: write, ok: false, status: null, ms: 0, reason: "" };
      var started = now();
      record.writes.push(entry);
      // A send that throws before it returns a promise is a failed write too.
      return Promise.resolve().then(send).then(function (status) {
        entry.ms = Math.max(0, Math.round(now() - started));
        entry.ok = true;
        entry.status = typeof status === "number" ? status : null;
      }, function (error) {
        entry.ms = Math.max(0, Math.round(now() - started));
        entry.status = error && typeof error.status === "number" ? error.status : null;
        entry.reason = error && error.message ? error.message : "cart_add_failed";
        throw error;
      });
    }

    function lastFailedWrite(record) {
      var failed = record.writes.filter(function (entry) { return !entry.ok; });
      return failed.length ? failed[failed.length - 1] : null;
    }

    // Measurement never blocks a sale: a page with no beacon, or a listener
    // that throws, costs the row and nothing else.
    function reportWriteTrouble(phase, record, last) {
      try {
        if (typeof win.CustomEvent !== "function" || !doc ||
            typeof doc.dispatchEvent !== "function") return;
        doc.dispatchEvent(new win.CustomEvent(CART_ADD_ERROR_EVENT, {
          detail: writeRecordDetail(phase, record, last)
        }));
      } catch (error) { /* the row is lost; the tap is not */ }
    }

    // R1d. The beacon detail: the three fields every `cart_add_error` has,
    // plus the write record. `observability.js` maps each key to its
    // `cart_add_*` field and the edge (`webhook-ingress/clientlog_scrub.py`)
    // checks each one alone. A `<write>Ms` key is the duration of that write in
    // the tap's LAST attempt, and is absent when the last attempt did not run
    // that write.
    var DURATION_KEYS = {
      clear: "clearMs", write1: "write1Ms", write2: "write2Ms", write_all: "writeAllMs"
    };

    function writeRecordDetail(phase, record, last) {
      var detail = {
        status: last && typeof last.status === "number" ? last.status : 0,
        phase: phase,
        retried: record.attempts > 1,
        attempts: record.attempts
      };
      if (last) {
        detail.failedWrite = last.write;
        detail.reason = last.reason;
        detail.failedWriteMs = last.ms;
      }
      record.writes.forEach(function (entry) {
        var key = DURATION_KEYS[entry.write];
        if (entry.attempt === record.attempts && key) detail[key] = entry.ms;
      });
      return detail;
    }

    // ---- R1b: the write sequence with its automatic retry -------------------

    function pause(ms) {
      if (typeof win.setTimeout !== "function") return Promise.resolve();
      return new Promise(function (resolve) { win.setTimeout(resolve, ms); });
    }

    // Attempt 1 is the path described above: clear -> write 1 -> write 2.
    // A retry sends JSON write 1 again ONLY if no earlier attempt got an ok
    // answer to it. Shopify emits `product_added_to_cart` only for an ok JSON
    // answer (R1-3 evidence), so a tap still reports at most one AddToCart
    // (N-5). After an ok write 1 the retry is clear -> ONE form write with ALL
    // lines and the attributes: zero pixel events, and atomic (a bad line
    // gives 422 and adds nothing, R1-3 evidence).
    //
    // The retries must stay inside the mutex SLOT, not only the entry: once
    // `bounded` releases the slot (MUTEX_HOLD_MS), a foreign writer such as the
    // free-shipping sweep can land between a retry's clear and its adds. So a
    // retry starts only when the time spent so far, its back-off and one more
    // attempt as slow as the last one still fit in the slot.
    function writeOffer(items, attributes, record) {
      var customerLineAnswered = false;
      var entered = now();
      function attempt(number) {
        record.attempts = number;
        return timed(record, number, "clear", clearForPurchase).then(function () {
          if (customerLineAnswered) {
            return timed(record, number, "write_all", function () {
              return formLinesWrite(items, attributes);
            });
          }
          return timed(record, number, "write1", function () {
            return customerWrite(items.slice(0, 1), attributes);
          }).then(function () {
            customerLineAnswered = true;
            if (items.length < 2) return null;
            // Write 2 runs only after write 1 is known good. Against a cart
            // that never took the customer line it would strand a free gift.
            return timed(record, number, "write2", function () {
              return formLinesWrite(items.slice(1));
            });
          });
        });
      }
      function run(number) {
        var started = now();
        return attempt(number).catch(function (error) {
          if (number >= WRITE_ATTEMPTS) throw error;
          var backoff = RETRY_BACKOFF_MS[number - 1];
          var finished = now();
          if ((finished - entered) + backoff + (finished - started) >= MUTEX_HOLD_MS) {
            record.retryStopped = "slot_budget";
            throw error;
          }
          return pause(backoff).then(function () {
            return run(number + 1);
          });
        });
      }
      return run(1);
    }

    // After the last failed attempt: today's `fail()` path, unchanged. The
    // record rides `buybox:fail` first, and a tap that reached the writes also
    // leaves one `cart_add_error` row.
    function refuse(record, error) {
      var reason = error && error.message ? error.message : "cart_add_failed";
      var last = lastFailedWrite(record);
      record.failedWrite = last ? last.write : null;
      record.status = last ? last.status : null;
      record.reason = reason;
      emit("fail", record);
      if (record.attempts) reportWriteTrouble(PHASE_FAILED, record, last);
      fail(reason);
      return false;
    }

    function purchase(oneTimeMode) {
      if (busy() || !availability()) return Promise.resolve(false);
      if (oneTimeMode && !state.config.oneTimeVariantId) return Promise.resolve(false);
      setBusy(true);
      var record = tapRecord(oneTimeMode);
      var items;
      try {
        items = oneTimeMode ? oneTimeItems() : checkoutItems();
      } catch (error) { return Promise.resolve(refuse(record, error)); }
      record.lines = items.length;
      // The write sequence, retries included, is ONE entry. The redirect stays
      // outside it, so the queue is released the moment the cart is right
      // rather than held across a navigation.
      return serialized(function () {
        // Read the latest head-writer facts after both queue waits.
        var attributes = oneTimeMode ? oneTimeAttributes() : checkoutAttributes();
        return writeOffer(items, attributes, record);
      }).then(function () {
        if (record.attempts > 1) {
          reportWriteTrouble(PHASE_RECOVERED, record, lastFailedWrite(record));
        }
        emit("add", record);
        redirect(oneTimeMode);
        return true;
      }).catch(function (error) {
        return refuse(record, error);
      });
    }

    function tap() {
      return purchase(false);
    }

    function oneTime() {
      return purchase(true);
    }

    // The V2 box emitted `checkout_entered` from buybox-page.js. The V3 cutover
    // deleted that file and nothing replaced the hook, so the direct-checkout
    // route has been invisible in GA4 since. Ruling R1 (countdown readout
    // 2026-09-20) settled that the route is intended product, so it has to be
    // COUNTED next to the drawer, not removed. The event crosses on a document
    // event; snippets/cart-drawer-analytics.liquid owns the one gtag path and
    // applies its own 1500 ms dedupe.
    //
    // D4 (countdown close-out plan 2026-09-21 §2). The announce used to sit one
    // statement before `location.href`, so the hit raced the unload and the
    // route stayed under-counted even after it was wired. The snippet now sends
    // it on `transport_type: 'beacon'` AND calls the ack below once gtag has the
    // hit; this function reports whether the navigation is worth deferring for.
    // It returns true only when something can plausibly ack — a page with no
    // gtag, no surface id or no CustomEvent navigates at once, exactly as
    // before. Measurement never delays the sale by more than the 300 ms cap.
    function announceCheckoutEntry(onDelivered, oneTimeMode) {
      try {
        if (!state.config.surfaceId || typeof win.CustomEvent !== "function") return false;
        var event = new win.CustomEvent("cw:checkout_entered", {
          detail: Object.assign({ surface: state.config.surfaceId, trigger: "direct-checkout" },
            oneTimeMode ? {} : selectionContext(state.selected))
        });
        if (typeof onDelivered === "function") event.cwCheckoutDelivered = onDelivered;
        doc.dispatchEvent(event);
        return typeof win.gtag === "function" && typeof win.setTimeout === "function";
      } catch (error) {
        return false;
      }
    }

    // The checkout URL, the price and the box behaviour are unchanged; only the
    // MOMENT of the navigation moves, and only by up to 300 ms.
    var CHECKOUT_ACK_CAP_MS = 300;

    function redirect(oneTimeMode) {
      var navigated = false;
      function navigate() {
        if (navigated) return;
        navigated = true;
        win.location.href = CHECKOUT_PATH;
      }
      var deferrable = announceCheckoutEntry(navigate, oneTimeMode);
      if (!deferrable) {
        navigate();
        return;
      }
      // Whichever comes first: the gtag delivery callback above, or the cap.
      try {
        win.setTimeout(navigate, CHECKOUT_ACK_CAP_MS);
      } catch (error) {
        navigate();
      }
    }

    // N-11. A failed write re-enables the CTA and gives the shopper one honest
    // next step, and that next step is RE-TAP.
    //
    // R-G7, folded in by wave F. The notice is Liquid: `buybox_surface.py`
    // renders the node, its heading and its body, `hidden`, with `role="alert"`
    // already on it. This file only clears `hidden`, so it builds no DOM and
    // writes no English at all. A page whose snippet predates that (an old
    // cached render) simply shows no notice; the CTA is still re-enabled below,
    // and the re-enabled CTA is what N-11 actually requires.
    //
    // FINDING A-3 (adversarial review 2026-09-18). The notice used to offer a
    // "Review cart" link to `/cart`. Write 2 (the gift + free-shipping form add)
    // can fail after write 1 has committed, and `/cart` then holds a routed paid
    // subscription line with NO gift and NO free-shipping line, which the orphan
    // cleanup calls healthy and the FS interceptor is not armed for: the shopper
    // checks out paying shipping against a card that reads "Free Shipping
    // Always". There is no state in which that link is the honest next step, so
    // it is gone. Re-tap is idempotent by R-1 -- every tap clears the cart
    // first -- so the re-enabled CTA is the whole recovery path.
    //
    // FINDING A-6 (adversarial review 2026-09-18). The notice is REVEALED
    // before the idle announcement, never after. The announcement can re-attach
    // the mount watcher to a replacement root, and a notice shown after it
    // lands on a root that is no longer the live box -- the shopper is told
    // nothing at all on exactly the failure the announcement exists for.
    function fail(reason) {
      var notice = state.root.querySelector("[data-buybox-notice]");
      if (notice) notice.hidden = false;
      setBusy(false);
      // A box replaced mid-purchase is left inert while this engine holds the
      // lock (see `mount`). When the write fails, the replacement must not stay
      // inert for good, so the release is announced and the mount watcher
      // attaches to whatever root is live now.
      if (typeof win.Event === "function" && typeof win.dispatchEvent === "function") {
        win.dispatchEvent(new win.Event("cw:buybox-idle"));
      }
      return notice;
    }

    return {
      boot: boot, teardown: teardown, tap: tap, oneTime: oneTime, fail: fail, onPageShow: onPageShow,
      setVariant: setVariant, checkoutItems: checkoutItems, oneTimeItems: oneTimeItems,
      state: state
    };
  }

  // The `product-rerender` scope that holds the box, and the form it watches.
  // Both are the theme's own contract (`theme.js`, ProductRerender).
  function scopeOf(root) {
    var node = root.parentNode;
    while (node && node.getAttribute) {
      if (String(node.tagName).toLowerCase() === "product-rerender") return node;
      node = node.parentNode;
    }
    return null;
  }

  function blockOf(root) {
    var node = root.parentNode;
    while (node && node.getAttribute) {
      if (node.getAttribute("data-block-id") !== null) return node;
      node = node.parentNode;
    }
    return null;
  }

  // `getAttribute`, never `.id`: the product form holds an input NAMED `id`
  // (the variant), so `form.id` is that input and not the form's id.
  function fromWatchedForm(root, event) {
    var scope = scopeOf(root);
    var form = scope && scope.getAttribute("observe-form");
    var target = event.target;
    return Boolean(form) && Boolean(target) && typeof target.getAttribute === "function" &&
      target.getAttribute("id") === form;
  }

  // BUG 1 of 2026-09-22. The flavour picker re-renders the section from the
  // PRODUCT url (`/products/<handle>?option_values=..&section_id=..`), and
  // `product-rerender` swaps every `liquid` block for the one in that
  // response. The surface snippet is gated on the PAGE path and template, so
  // the product-url response carries an EMPTY block, and swapping it in
  // deleted the live box. So the box's block is taken out of the fragment
  // before the swap: the theme finds no match and leaves the live box -- its
  // pressed card, its engine and its lock -- exactly where it was. Capture
  // phase on the document runs before the form's own listener. A PRODUCT
  // change replaces the whole scope and is left to the theme.
  function keepBox(doc, event) {
    var root = doc.querySelector("[data-buybox]");
    var detail = event.detail || {};
    var fragment = detail.htmlFragment;
    if (!root || detail.productChange || !fragment ||
        typeof fragment.querySelectorAll !== "function" ||
        !fromWatchedForm(root, event)) return false;
    var block = blockOf(root);
    var blockId = block && block.getAttribute("data-block-id");
    if (!blockId) return false;
    var removed = false;
    Array.prototype.slice.call(fragment.querySelectorAll("[data-block-id]"))
      .forEach(function (node) {
        if (node.getAttribute("data-block-id") !== blockId) return;
        node.remove();
        removed = true;
      });
    return removed;
  }

  // The box is watched, not mounted once: a root that appears later (a
  // deferred section, a cached render) still gets its own engine, which opens
  // on the card the server rendered as pressed. A box replaced mid-purchase is
  // NOT re-booted -- the engine holding the lock owns the write until it
  // reports, and the inert markup is the honest thing to show meanwhile. A
  // flavour change no longer replaces the box at all (`keepBox`); it re-points
  // the live engine at the new variant instead.
  function mount(win) {
    if (win.__cwBuyboxMounted) return;
    win.__cwBuyboxMounted = true;
    var doc = win.document;
    var live = null;
    var attemptedRoot = null;
    var attach = function () {
      var root = doc.querySelector("[data-buybox]");
      if (win.__cwBuybox && win.__cwBuybox.busy) return;
      if (live && live.state.root !== root) {
        live.teardown();
        live = null;
      }
      if (!root) { attemptedRoot = null; return; }
      if (root === attemptedRoot) return;
      // Invalid markup stays inert; mutation batches never retry the same root.
      attemptedRoot = root;
      live = createBuybox({ window: win, document: doc });
      if (!live.boot(root)) live = null;
    };
    win.addEventListener("pageshow", function () { if (live) live.onPageShow(); });
    win.addEventListener("cw:buybox-idle", attach);
    doc.addEventListener("product:rerender", function (event) { keepBox(doc, event); }, true);
    doc.addEventListener("variant:change", function (event) {
      if (!live || !live.state.root || !fromWatchedForm(live.state.root, event)) return;
      live.setVariant((event.detail || {}).variant);
    });
    if (typeof win.MutationObserver === "function") {
      new win.MutationObserver(attach).observe(doc.documentElement, {
        childList: true, subtree: true
      });
    }
    // The inline script follows the box markup: bind it during this parse,
    // including browsers without MutationObserver. Keep watching later roots.
    attach();
    if (doc.readyState === "loading") {
      doc.addEventListener("DOMContentLoaded", attach);
    }
  }

  globalScope.createBuybox = createBuybox;
  globalScope.mountBuybox = mount;
  if (typeof module === "object" && module.exports) {
    module.exports = {
      createBuybox: createBuybox, mountBuybox: mount,
      formBody: formBody, formAccepted: formAccepted
    };
  } else if (typeof window !== "undefined") {
    mount(window);
  }
}(typeof globalThis === "object" ? globalThis : this));
